// src/pages/Support/Support.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth.js';
import { useToast } from '../../hooks/useToast.js';
import { supportAPI } from '../../api/support.js';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { formatDate } from '../../utils/formatters.js';
import { REQUISITES } from '../../utils/requisites.js';

export const Support = () => {
  const { user, isAuthenticated } = useAuth();
  const { success, error } = useToast();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.first_name ? `${user.first_name} ${user.last_name || ''}` : '',
    email: user?.email || '',
    phone: user?.phone || '',
    subject: '',
    message: '',
    category: 'other',
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      loadTickets();
    }
  }, [isAuthenticated]);

  const loadTickets = async () => {
    setIsLoading(true);
    try {
      const response = await supportAPI.getMy({ limit: 50 });
      setTickets(response.data.data || []);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      error('Заполните тему и сообщение');
      return;
    }

    setIsSubmitting(true);
    try {
      await supportAPI.create(formData);
      success('Обращение отправлено');
      setShowForm(false);
      setFormData({
        ...formData,
        subject: '',
        message: '',
      });
      if (isAuthenticated) {
        loadTickets();
      }
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось отправить обращение');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddResponse = async (ticketId) => {
    if (!responseText.trim()) {
      error('Введите текст ответа');
      return;
    }

    try {
      await supportAPI.addResponse(ticketId, { response: responseText });
      success('Ответ добавлен');
      setResponseText('');
      loadTickets();
      setSelectedTicket(null);
    } catch (err) {
      error('Не удалось добавить ответ');
    }
  };

  const breadcrumbs = [{ label: 'Поддержка', path: '/support' }];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '24px',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'rgb(var(--c-text))',
            }}>
              Поддержка
            </h1>
            <p style={{
              fontSize: '16px',
              fontWeight: 300,
              color: 'rgb(var(--c-text-secondary))',
              marginTop: '4px',
            }}>
              {isAuthenticated ? 'Ваши обращения' : 'Свяжитесь с нами'}
            </p>
          </div>
          {isAuthenticated && (
            <Button onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Закрыть форму' : 'Новое обращение'}
            </Button>
          )}
        </div>

        {/* Форма обращения */}
        {showForm && (
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            border: '1px solid rgb(var(--c-border))',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'rgb(var(--c-text))',
              marginBottom: '16px',
            }}>
              Новое обращение
            </h3>

            <form onSubmit={handleSubmit}>
              {!isAuthenticated && (
                <>
                  <Input
                    label="Имя *"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                  <div style={{ height: '12px' }} />
                  <Input
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                  <div style={{ height: '12px' }} />
                  <Input
                    label="Телефон"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <div style={{ height: '12px' }} />
                </>
              )}

              <Input
                label="Тема *"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              />
              <div style={{ height: '12px' }} />

              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '4px',
                }}>
                  Сообщение *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Опишите вашу проблему..."
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgb(var(--c-border))',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '120px',
                    fontFamily: 'inherit',
                    backgroundColor: 'rgb(var(--c-bg))',
                    color: 'rgb(var(--c-text))',
                  }}
                  required
                />
              </div>

              <Button type="submit" fullWidth isLoading={isSubmitting}>
                Отправить обращение
              </Button>
            </form>
          </div>
        )}

        {/* Список обращений (для авторизованных) */}
        {isAuthenticated ? (
          isLoading ? (
            <Loader text="Загрузка обращений..." />
          ) : tickets.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 0',
              backgroundColor: 'rgb(var(--c-surface))',
              borderRadius: '20px',
              border: '1px solid rgb(var(--c-border))',
            }}>
              <p style={{ fontSize: '18px', color: 'rgb(var(--c-text-secondary))' }}>У вас нет обращений</p>
              <Button onClick={() => setShowForm(true)} style={{ marginTop: '16px' }}>
                Создать обращение
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  style={{
                    backgroundColor: 'rgb(var(--c-surface))',
                    border: '1px solid rgb(var(--c-border))',
                    borderRadius: '16px',
                    padding: '20px 24px',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}>
                    <div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: 'rgb(var(--c-text))',
                      }}>
                        {ticket.subject}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: 'rgb(var(--c-text-secondary))',
                        marginTop: '4px',
                      }}>
                        {formatDate(ticket.createdAt)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: ticket.status === 'closed' ? '#6B6B6B' :
                                      ticket.status === 'resolved' ? '#28A745' :
                                      ticket.status === 'in_progress' ? '#FFC107' :
                                      '#205CA0',
                        color: 'rgb(var(--c-on-accent))',
                      }}>
                        {ticket.status === 'open' ? 'Открыто' :
                         ticket.status === 'in_progress' ? 'В работе' :
                         ticket.status === 'waiting' ? 'Ожидает' :
                         ticket.status === 'resolved' ? 'Решено' :
                         ticket.status === 'closed' ? 'Закрыто' : ticket.status}
                      </span>
                      {ticket.assigned_to && (
                        <span style={{
                          fontSize: '12px',
                          color: 'rgb(var(--c-text-secondary))',
                        }}>
                          👤 Назначено
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{
                    marginTop: '12px',
                    fontSize: '14px',
                    color: 'rgb(var(--c-text-secondary))',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {ticket.message}
                  </div>

                  {ticket.response && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px 16px',
                      backgroundColor: 'rgb(var(--c-surface-2))',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: 'rgb(var(--c-text))',
                    }}>
                      <strong>Ответ:</strong> {ticket.response}
                    </div>
                  )}

                  <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}>
                    <button
                      onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgb(var(--c-accent))',
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      {selectedTicket?.id === ticket.id ? 'Закрыть' : 'Ответить'}
                    </button>
                    {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                      <button
                        onClick={async () => {
                          try {
                            await supportAPI.close(ticket.id, { resolution_note: 'Закрыто пользователем' });
                            success('Обращение закрыто');
                            loadTickets();
                          } catch (err) {
                            error('Не удалось закрыть обращение');
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgb(var(--c-danger))',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        Закрыть обращение
                      </button>
                    )}
                  </div>

                  {/* Форма ответа */}
                  {selectedTicket?.id === ticket.id && (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: 'rgb(var(--c-bg))',
                      borderRadius: '12px',
                    }}>
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Введите ответ..."
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgb(var(--c-border))',
                          fontSize: '14px',
                          outline: 'none',
                          resize: 'vertical',
                          minHeight: '80px',
                          fontFamily: 'inherit',
                          backgroundColor: 'rgb(var(--c-surface))',
                          color: 'rgb(var(--c-text))',
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAddResponse(ticket.id)}
                        style={{ marginTop: '8px' }}
                      >
                        Отправить ответ
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          // Для неавторизованных пользователей показываем контактную информацию
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            border: '1px solid rgb(var(--c-border))',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: 'rgb(var(--c-text))',
              marginBottom: '16px',
            }}>
              Свяжитесь с нами
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'rgb(var(--c-text-secondary))',
              marginBottom: '8px',
            }}>
              📞 8 904 095 01 10
            </p>
            <p style={{
              fontSize: '16px',
              color: 'rgb(var(--c-text-secondary))',
              marginBottom: '24px',
            }}>
              {/* ⚠️ Домен исправлен 2026-10-07: было `info@studio-reklama.ru`.
                  Проверено замером: у этого домена НЕТ A-записи, он не
                  существует. Наш домен — `reklama-oskol.ru`. */}
              📧 {REQUISITES.email}
            </p>
            <Button onClick={() => setShowForm(true)}>
              Написать нам
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};